import invoicesService from "@/modules/invoices/services/invoicesService";
import { i18n } from "@/plugins/i18n";
import { CURRENCIES } from '@/constansts/currency';
import taxUtil, { TAX_MODES } from '@/modules/payments/utils/taxUtil';
import { calcDocumentTaxes, TAX_STRATEGIES } from '@/modules/payments/utils/taxCalculator';
import formattingUtil from '@/utils/formattingUtil';

const getTaxMode = (invoice) => (
  invoice?.metadata?.tax_mode === TAX_MODES.include ? TAX_MODES.include : TAX_MODES.exclude
);

const discountedUnitAmount = (item, taxMode) => {
  const lineTotal = taxUtil.getPriceByTaxMode(taxMode, item.net_total, item.grand_total);
  if (lineTotal == null || !item.quantity) return item.rate;
  return formattingUtil.roundNumber(lineTotal / item.quantity);
};

const buildRefundCreditNotePayload = async ({ invoiceUid, sourcePaymentUid, refundAmount, invoiceOriginalAmount }) => {
  const isFullRefund = parseFloat(refundAmount) === parseFloat(invoiceOriginalAmount);

  if (isFullRefund) {
    const invoice = await invoicesService.fetchInvoice(invoiceUid);
    const taxMode = getTaxMode(invoice);
    const invoiceLineItems = invoice.items.concat(
      invoice.sections?.flatMap((section) => section.items) || [],
    );

    return {
      invoice_uid: invoiceUid,
      line_items: invoiceLineItems.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unit_amount: discountedUnitAmount(item, taxMode),
        net_total: item.net_total,
        taxes: item.taxes,
      })),
      notes: i18n.t("refund_dialog.credit_note_reason.full_refund"),
      source_payment_uid: sourcePaymentUid,
    };
  }

  return {
    invoice_uid: invoiceUid,
    line_items: [{
      name: i18n.t("refund_dialog.credit_note_partial_refund_item_name"),
      quantity: 1,
      unit_amount: refundAmount,
    }],
    notes: i18n.t("refund_dialog.credit_note_reason.partial_refund"),
    source_payment_uid: sourcePaymentUid,
  };
};

const getCreditNoteInvoice = (state) => {
  const invoiceUid = state.creditNoteEditor?.invoice_uid;
  return state.invoices[invoiceUid];
};

const getAvailableCreditAmount = (state) => {
  const invoice = getCreditNoteInvoice(state);
  if (!invoice?.payment_status || !invoice?.amount) return 0;

  const total = parseFloat(invoice.amount) || 0;
  const credited = parseFloat(invoice.payment_status.amount_credited) || 0;
  return formattingUtil.roundNumber(total - credited);
};

// True while a credit note line still credits its invoice item whole - same quantity, same unit
// amount, and every tax on it settled. Such a line can carry the invoice's own cents, which makes
// the credit note equal the invoice whichever way the invoice was rounded.
const matchesInvoiceItem = (item) => item.isFromInvoice
  && item.net_total != null
  && item.quantity === item.maxQuantity
  && item.unit_amount === item.originalUnitAmount
  && (item.taxes || []).every((tax) => tax.tax_total != null);

// Core decides this per document, not per line: anything edited, or any tax total missing, and
// every line recomputes from its rate (Itemizable#should_reconcile_taxes?).
const carriesInvoiceTaxes = (items) => items.length > 0 && items.every(matchesInvoiceItem);

// One credit note line as the tax calculation reads it.
const creditNoteTaxLine = (item, taxMode, fixedTaxes) => {
  const taxes = item.taxes || [];
  if (!fixedTaxes) {
    return { base: item.unit_amount * item.quantity, taxes };
  }

  const taxTotal = taxes.reduce((acc, tax) => acc + (parseFloat(tax.tax_total) || 0), 0);
  return {
    base: taxUtil.getPriceByTaxMode(taxMode, item.net_total, item.net_total + taxTotal),
    taxes,
    fixed_taxes: true,
  };
};

// One computation behind both what the editor shows and what the credit note is issued with,
// so the taxes the user approves are the taxes that are sent.
const calcCreditEditorTaxes = (state, strategy) => {
  const taxMode = getTaxMode(getCreditNoteInvoice(state));
  const items = state.creditNoteEditor?.line_items || [];
  const fixedTaxes = carriesInvoiceTaxes(items);

  return calcDocumentTaxes({
    lines: items.map((item) => creditNoteTaxLine(item, taxMode, fixedTaxes)),
    taxMode,
    strategy,
  });
};

const calcCreditEditorBreakdown = (state, strategy) => {
  if (!state.creditNoteEditor?.line_items?.length || !getCreditNoteInvoice(state)) {
    return { netTotal: 0, taxTotal: 0, grandTotal: 0, taxes: [] };
  }

  const totals = calcCreditEditorTaxes(state, strategy);

  return {
    netTotal: totals.net_total,
    taxTotal: totals.tax_total,
    grandTotal: totals.grand_total,
    taxes: totals.taxes,
  };
};

// The line as the credit note is issued: its net and its tax carried explicitly, because a
// unit_amount alone cannot express a 100.00 net over a quantity of 3.
const creditNoteLineItems = (state, strategy) => {
  const items = state.creditNoteEditor?.line_items || [];
  const totals = calcCreditEditorTaxes(state, strategy);

  return items.map((item, index) => ({
    name: item.name,
    quantity: item.quantity,
    unit_amount: item.unit_amount,
    net_total: totals.lines[index].net_total,
    taxes: (item.taxes || []).map((tax, taxIndex) => ({
      ...tax,
      tax_total: totals.lines[index].taxes[taxIndex].tax_total,
    })),
  }));
};

const taxStrategy = (rootGetters) => (rootGetters['BusinessStore/hasFeature']('rollout.payments.tax_group_reconciliation')
  ? TAX_STRATEGIES.grouped
  : TAX_STRATEGIES.perLine);

const state = {
  invoices: {},
  creditNoteEditor: null,
};

const getters = {
  invoiceByUid: (state) => (uid) => state.invoices[uid],
  creditNoteEditor: (state) => state.creditNoteEditor,
  creditNoteCurrencySymbol: (state) => {
    const invoice = getCreditNoteInvoice(state);
    return CURRENCIES[invoice?.currency]?.symbol || '';
  },
  creditAvailable: (state) => {
    const invoice = getCreditNoteInvoice(state);
    if (!invoice?.currency) return '';

    const available = getAvailableCreditAmount(state);
    return formattingUtil.priceDisplay(available, invoice.currency);
  },
  creditEditorSummary: (state, getters, rootState, rootGetters) => {
    const { netTotal, grandTotal, taxes } = calcCreditEditorBreakdown(state, taxStrategy(rootGetters));
    const invoice = getCreditNoteInvoice(state);
    const currency = invoice?.currency;

    const formatPrice = (amount) => formattingUtil.priceDisplay(amount || 0, currency);

    return {
      subtotal: formatPrice(netTotal),
      taxes: taxes
        .filter((tax) => tax.tax_total > 0)
        .map((tax) => ({
          name: tax.description,
          rate: tax.rate,
          amount: formatPrice(tax.tax_total),
        })),
      total: formatPrice(grandTotal),
    };
  },
  isCreditExceedingLimit: (state, getters, rootState, rootGetters) => {
    const availableCredit = getAvailableCreditAmount(state);
    const { grandTotal } = calcCreditEditorBreakdown(state, taxStrategy(rootGetters));
    return grandTotal > availableCredit;
  },
};

const mutations = {
  setInvoice(state, invoice) {
    state.invoices = {
      ...state.invoices,
      [invoice.uid]: invoice,
    };
  },
  updateInvoice(state, updatedFields) {
    state.invoices = {
      ...state.invoices,
      [updatedFields.uid]: {
        ...state.invoices[updatedFields.uid],
        ...updatedFields,
      },
    };
  },
  setCreditNoteEditor(state, creditNote) {
    state.creditNoteEditor = creditNote;
  },
  updateCreditNoteEditor(state, updates) {
    state.creditNoteEditor = {
      ...state.creditNoteEditor,
      ...updates,
    };
  },
  resetCreditNoteEditor(state) {
    state.creditNoteEditor = null;
  },
};

const actions = {
  async fetchInvoice({ commit }, invoiceUid) {
    const invoice = await invoicesService.fetchInvoice(invoiceUid);
    commit("setInvoice", invoice);
    return invoice;
  },
  initializeCreditNoteEditor({ commit, state }, invoiceUid) {
    const invoice = state.invoices[invoiceUid];
    if (!invoice) return;

    const hasPreviousCreditNotes = parseFloat(invoice.payment_status?.amount_credited) > 0;

    let lineItems = [];

    if (hasPreviousCreditNotes) {
      lineItems = [{
        id: `custom-${Date.now()}`,
        name: '',
        quantity: 1,
        unit_amount: 0,
        taxes: [],
        isFromInvoice: false,
      }];
    } else {
      const taxMode = getTaxMode(invoice);
      const invoiceLineItems = (invoice.items || []).concat(
        invoice.sections?.flatMap((section) => section.items) || [],
      );

      lineItems = invoiceLineItems.map((item, index) => ({
        id: `${invoiceUid}-${index}`,
        name: item.name,
        quantity: item.quantity,
        unit_amount: discountedUnitAmount(item, taxMode),
        net_total: item.net_total,
        taxes: item.taxes || [],
        isFromInvoice: true,
        maxQuantity: item.quantity,
        originalUnitAmount: discountedUnitAmount(item, taxMode),
      }));
    }

    const creditNote = {
      invoice_uid: invoiceUid,
      line_items: lineItems,
      notes: '',
    };

    commit('setCreditNoteEditor', creditNote);
  },
  resetCreditNoteEditor({ commit }) {
    commit('resetCreditNoteEditor');
  },
  updateCreditNoteEditor({ commit }, updates) {
    commit('updateCreditNoteEditor', updates);
  },
  updateCreditNoteLineItem({ commit, state }, { itemId, updates }) {
    const lineItems = state.creditNoteEditor?.line_items || [];
    const updatedLineItems = lineItems.map((item) => {
      if (item.id !== itemId) return item;
      return { ...item, ...updates };
    });
    commit('updateCreditNoteEditor', { line_items: updatedLineItems });
  },
  addCustomCreditNoteItem({ commit, state }) {
    const lineItems = state.creditNoteEditor?.line_items || [];
    const newItem = {
      id: `custom-${Date.now()}`,
      name: '',
      quantity: 1,
      unit_amount: 0,
      taxes: [],
      isFromInvoice: false,
    };
    commit('updateCreditNoteEditor', { line_items: [...lineItems, newItem] });
  },
  removeCreditNoteLineItem({ commit, dispatch, state }, itemId) {
    const lineItems = state.creditNoteEditor?.line_items || [];
    const updatedLineItems = lineItems.filter((item) => item.id !== itemId);
    commit('updateCreditNoteEditor', { line_items: updatedLineItems });
    if (updatedLineItems.length === 0) {
      dispatch('addCustomCreditNoteItem');
    }
  },
  clearCreditNoteItems({ commit, dispatch }) {
    commit('updateCreditNoteEditor', { line_items: [] });
    dispatch('addCustomCreditNoteItem');
  },
  async cancelInvoice({ commit }, { payableUid, payload }) {
    const cancelInvoiceResponse = await invoicesService.cancelInvoice(payableUid, payload);
    commit("updateInvoice", cancelInvoiceResponse.data.invoice);
    return cancelInvoiceResponse;
  },
  async createCreditNote(
    { dispatch, state, rootGetters },
    { creditNotePayload, invoiceUid, sourcePaymentUid, refundAmount, invoiceOriginalAmount }
  ) {
    const payload = creditNotePayload
      ? { ...creditNotePayload, line_items: creditNoteLineItems(state, taxStrategy(rootGetters)) }
      : await buildRefundCreditNotePayload({ invoiceUid, sourcePaymentUid, refundAmount, invoiceOriginalAmount });

    const response = await invoicesService.createCreditNote(payload);

    if (sourcePaymentUid) {
      dispatch("PaymentsStore/fetchPayment", { uid: sourcePaymentUid }, { root: true });
    }

    return response;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
